import { useCallback, useMemo, useRef, useState } from 'react';

import { useFeatureFlagOverrides, useSgLdClient } from '../hooks';
import {
  type BaseFeatureFlags,
  type LaunchDarklyContext,
  type LaunchDarklyCustomer,
  type LaunchDarklyMultiContext,
} from '../launch-darkly.types';
import { formatContextEntry, generateAnonymousUserContext, generateUserContext } from '../utils';
import { type LaunchDarklyProviderContextType as ContextType } from './launch-darkly.context';
import { type InnerProviderProps } from './launch-darkly.provider.types';

// ─────────────────────────────────────────────────────────────────────────────

export const LaunchDarklyContextProvider = <FeatureFlags extends BaseFeatureFlags>(
  props: InnerProviderProps<FeatureFlags>,
) => {
  const { Context, defaultFlags, localOverrides, children } = props;

  const client = useSgLdClient();
  const overrides = useFeatureFlagOverrides<FeatureFlags>({ localOverrides });

  const { allOverrides, clearFeatureFlagOverrides, overrideFeatureFlag } = overrides;

  // ─── State ───────────────────────────────────────────────────────────

  // A flag to track if a context has been identified. Can be helpful to defer
  // components rendering until at least one context is identified.
  const [hasIdentifiedContext, setHasIdentifiedContext] = useState(false);

  // ─── Refs ────────────────────────────────────────────────────────────

  const currentMultiContextRef = useRef<LaunchDarklyMultiContext | undefined>(undefined);

  // A ref to hold the last promise for serialization
  const addContextsQueueRef = useRef<Promise<void>>(Promise.resolve());

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Add multiple contexts to the LaunchDarkly client.
   *
   * It is also used to serialize the calls to the LaunchDarkly client to avoid
   * race conditions and updates.
   */
  const addContexts = useCallback<ContextType<FeatureFlags>['addContexts']>(
    async (contexts) => {
      // chain this call onto the previous one
      addContextsQueueRef.current = addContextsQueueRef.current.then(async () => {
        const contextEntries = Object.entries(contexts);
        const formatContextPromises = contextEntries.map(formatContextEntry);
        const formattedContextsEntries = await Promise.all(formatContextPromises); // prettier-ignore
        const formattedContexts = Object.fromEntries(formattedContextsEntries); // prettier-ignore

        const updatedMultiContext: LaunchDarklyMultiContext = {
          ...currentMultiContextRef.current,
          kind: 'multi',
          ...formattedContexts,
        };

        currentMultiContextRef.current = updatedMultiContext;

        await client.identify(updatedMultiContext);
      });

      // wait for this call to finish before returning
      await addContextsQueueRef.current;

      setHasIdentifiedContext(true);
    },
    [client],
  );

  /**
   * Add a single context to the LaunchDarkly client.
   */
  const addContext = useCallback<ContextType<FeatureFlags>['addContext']>(
    async (contextKind, context) => {
      await addContexts({ [contextKind]: context });
    },
    [addContexts],
  );

  const identifyCustomer = useCallback(
    async (customer: LaunchDarklyCustomer | undefined, extraContext?: LaunchDarklyContext) => {
      const isValidCustomer = customer?.id !== undefined;

      const context = isValidCustomer
        ? generateUserContext({ customer })
        : generateAnonymousUserContext();

      await addContext('user', { ...context, ...extraContext });

      currentMultiContextRef.current = {
        ...currentMultiContextRef.current,
        kind: 'multi',
        user: context,
      };
    },
    [addContext],
  );

  const resetCustomer = useCallback(
    async (extraContext?: LaunchDarklyContext) => {
      await identifyCustomer(undefined, extraContext);
    },
    [identifyCustomer],
  );

  // ─────────────────────────────────────────────────────────────────────

  /**
   * We memoize the value here to avoid re-rendering the context provider
   * when the context is updated.
   *
   * This is important because the context provider is used to provide the
   * context to the children of the provider.
   */
  const value = useMemo<ContextType<FeatureFlags>>(() => {
    return {
      isLaunchDarklyReady: client.isReady,
      isLaunchDarklyTargetingContextSet: hasIdentifiedContext,
      defaultFlags,
      flagOverrides: allOverrides,
      identifyCustomer,
      resetCustomer,
      overrideFeatureFlag,
      clearFeatureFlagOverrides,
      addContext,
      addContexts,
    };
  }, [
    client.isReady,
    allOverrides,
    defaultFlags,
    hasIdentifiedContext,
    addContext,
    addContexts,
    identifyCustomer,
    resetCustomer,
    overrideFeatureFlag,
    clearFeatureFlagOverrides,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
